import { useEffect, type ReactNode } from 'react';
import { PublicHeader } from './PublicHeader';
import { SiteFooter } from './SiteFooter';
import { useCanonical } from '../lib/canonical';
import '../styles/landing.css';
import '../styles/public.css';
import '../styles/legal.css';

/**
 * The shell both legal documents share.
 *
 * Two of them exist and they will be edited by someone who is not a developer,
 * so the layout, the contents list and the anchor behaviour live here once and
 * each document is only its sections. Adding a section to either page is
 * adding an object to an array; the contents list and its deep link follow.
 *
 * Anchors matter more here than anywhere else on the site. A privacy policy is
 * cited, not browsed — the footer already links to /privacy#odpc, and a
 * regulator or a tenant asking about one clause should land on that clause.
 */

export interface LegalSection {
  /** URL fragment. Stable: these get linked to from outside the site. */
  id: string;
  heading: string;
  body: ReactNode;
}

export interface LegalDocProps {
  title: string;
  /** One sentence under the title saying what the document is for. */
  lede: string;
  effectiveDate: string;
  sections: LegalSection[];
  /**
   * Shown until a Kenyan advocate has reviewed the text.
   *
   * Deliberately prominent rather than a footnote: a policy that has not been
   * reviewed should not be able to pass for one that has, and the people most
   * likely to rely on it — a tenant, a regulator — are exactly the people a
   * quiet disclaimer would fail.
   */
  draft?: boolean;
  canonicalPath: string;
}

export function LegalDoc({
  title, lede, effectiveDate, sections, draft, canonicalPath,
}: LegalDocProps) {
  useCanonical(canonicalPath);

  // Land on the cited clause when someone arrives with a fragment. The router
  // renders after the browser has already tried and failed to find it.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    const target = document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [sections]);

  return (
    <div className="lp mk">
      <PublicHeader />

      <div className="legal">
        <header className="legal-head">
          <h1 className="mk-title">{title}</h1>
          <p className="mk-lede">{lede}</p>
          <p className="legal-date">Effective {effectiveDate}</p>
        </header>

        {draft && (
          <div className="legal-draft" role="note">
            <strong>This is a draft.</strong> It describes accurately how NyumbaSuite
            works today, but it has not yet been reviewed by an advocate qualified in
            Kenyan law. Please do not rely on it as a final statement of your rights or
            ours until this notice is removed.
          </div>
        )}

        <nav className="legal-toc" aria-label="Contents">
          <h2>Contents</h2>
          <ol>
            {sections.map((s, i) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>{i + 1}. {s.heading}</a>
              </li>
            ))}
          </ol>
        </nav>

        {sections.map((s, i) => (
          <section key={s.id} id={s.id} className="legal-section">
            <h2><span className="legal-num">{i + 1}.</span> {s.heading}</h2>
            {s.body}
          </section>
        ))}
      </div>

      <SiteFooter />
    </div>
  );
}

/**
 * The one place the company's own details live.
 *
 * Both documents read from this, so filling it in once fills in both. Every
 * value marked TO BE CONFIRMED is a fact only NyumbaSuite can supply — a
 * registration number cannot be inferred, and inventing one in a legal
 * document would be worse than leaving it visibly blank.
 */
export const COMPANY = {
  tradingName: 'NyumbaSuite',
  legalName: 'NyumbaSuite (legal entity name TO BE CONFIRMED)',
  registration: 'Company registration number TO BE CONFIRMED',
  address: 'Registered address TO BE CONFIRMED, Nairobi, Kenya',
  email: 'nyumbasuite@gmail.com',
  privacyEmail: 'nyumbasuite@gmail.com',
  /** Registration as a data controller/processor with the ODPC. */
  odpcRegistration: 'ODPC registration number TO BE CONFIRMED',
};

/** Marks a value the company still has to supply, so it cannot be missed. */
export function ToConfirm({ children }: { children: ReactNode }) {
  return <span className="legal-tbc" title="To be confirmed before publication">{children}</span>;
}
